import { useEffect, useRef, useState } from "react";
import { clamp, motionOn } from "./env";

type Props = {
  value: number;
  /** Receives intermediate (fractional) values while counting — round inside. */
  format: (n: number) => string;
  suffix?: string;
  className?: string;
};

const easeOutCubic = (k: number) => 1 - Math.pow(1 - k, 3);

// Count-up number. The prerendered / hydrated text is the FINAL value (never 0 — lessons 0018,
// 0037). After mount, with motion on, it resets to 0 and counts up when the nearest
// [data-play="count"] ancestor receives `ns:play`. Later value changes (live stats arriving)
// count from the shown value. All updates write the DOM directly: React renders the text once
// and never touches it again, so there is no re-render per frame and no reconciliation clash.
export function CountUp({ value, format, suffix = "", className }: Props) {
  const ref = useRef<HTMLSpanElement>(null);
  const [initial] = useState(() => format(value) + suffix);
  const fmt = useRef({ format, suffix });
  useEffect(() => {
    fmt.current = { format, suffix };
  });
  const st = useRef({ shown: value, target: value, armed: false, played: false, raf: 0 });
  const text = format(value) + suffix;

  const paint = (n: number) => {
    if (ref.current) ref.current.textContent = fmt.current.format(n) + fmt.current.suffix;
  };

  const animate = (from: number, to: number, dur: number) => {
    const s = st.current;
    cancelAnimationFrame(s.raf);
    const t0 = performance.now();
    const step = (t: number) => {
      const k = clamp((t - t0) / dur, 0, 1);
      s.shown = from + (to - from) * easeOutCubic(k);
      paint(k < 1 ? s.shown : to);
      s.raf = k < 1 ? requestAnimationFrame(step) : 0;
    };
    s.raf = requestAnimationFrame(step);
  };

  // Arm: reset to 0 and wait for the section's play event.
  useEffect(() => {
    const s = st.current;
    const host = ref.current?.closest<HTMLElement>('[data-play="count"]');
    if (!motionOn() || !host) return;
    s.armed = true;
    s.shown = 0;
    paint(0);
    const onPlay = () => {
      s.played = true;
      animate(0, s.target, 1400);
    };
    host.addEventListener("ns:play", onPlay, { once: true });
    return () => {
      host.removeEventListener("ns:play", onPlay);
      cancelAnimationFrame(s.raf);
      s.armed = false;
      s.played = false;
      s.shown = s.target;
      paint(s.target);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- arm once per mount
  }, []);

  // New target (live stats) or new formatting (language): count or repaint.
  useEffect(() => {
    const s = st.current;
    s.target = value;
    if (s.armed && !s.played) return; // the pending play counts to the new target
    if (s.played && motionOn() && s.shown !== value) animate(s.shown, value, 800);
    else if (!s.raf) {
      s.shown = value;
      paint(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `text` covers format/suffix changes
  }, [value, text]);

  return (
    <span ref={ref} className={className}>
      {initial}
    </span>
  );
}
